<template>
  <v-app id="inspire" style="background: #f8f9fb">
    <v-main class="image">
      <v-container class="fill-height">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="12" md="6">
            <app-header />
            <v-card class="auth-card mx-auto" max-width="460" elevation="0">
              <div class="auth-head">
                <h2 class="auth-title">Welcome back</h2>
                <p class="auth-sub">Sign in to continue to your dashboard</p>
              </div>
              <v-form v-model="valid">
                <v-text-field
                  label="Email"
                  name="email"
                  id="email"
                  prepend-inner-icon="mdi-email-outline"
                  type="text"
                  v-model="email"
                  :rules="[required('email'), emailFormat()]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="show ? 'text' : 'password'"
                  id="password"
                  label="Password"
                  name="password"
                  prepend-inner-icon="mdi-lock-outline"
                  v-model="password"
                  @click:append="show = !show"
                  :rules="[required('password'), minLength('password', 8)]"
                  counter="true"
                  outlined
                  dense
                ></v-text-field>
                <div class="tw-flex tw-justify-end">
                  <router-link to="/forgotpassword" class="auth-link">
                    Forgot password?
                  </router-link>
                </div>
                <v-slide-y-transition>
                  <div v-if="errorMessage" class="auth-error" role="alert">
                    <v-icon small color="#b3251b"
                      >mdi-alert-circle-outline</v-icon
                    >
                    <span>{{ errorMessage }}</span>
                  </div>
                </v-slide-y-transition>
                <v-btn
                  block
                  large
                  id="signIn"
                  class="auth-btn mt-4"
                  @click="onSubmit"
                  :loading="loading"
                  :disabled="!valid"
                >
                  Login
                </v-btn>
              </v-form>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
    <app-bottom />
  </v-app>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import validation from '@/utils/Validations'

export default {
  components: {
    appHeader: () => import('@/components/Logo'),
    appBottom: () => import('@/components/BottomImage'),
  },
  props: {
    source: String,
  },
  data() {
    return {
      valid: false,
      email: '',
      password: '',
      show: false,
      errorMessage: '',
      ...validation,
    }
  },
  created() {
    this.$store.commit('auth/setNewPassword', '')
  },
  computed: {
    alertError() {
      return this.$store.getters.alertError
    },
    ...mapState(['loading']),
    ...mapState('auth', ['createUser']),
    ...mapGetters('auth', ['hasAuthenticationStatus', 'authenticationStatus']),
  },
  methods: {
    async onSubmit() {
      this.$store.state.loading = true
      if (!this.valid) return

      this.errorMessage = ''
      this.$store.commit('SET_LOADING', true)

      try {
        const formData = {
          username: this.email,
          password: this.password,
          email: this.email,
        }

        await this.$store.dispatch('auth/signIn', formData)
        this.$store.state.loading = false

        if (
          this.createUser &&
          this.createUser.challengeName === 'NEW_PASSWORD_REQUIRED'
        ) {
          this.$router.push({ name: 'CreatePassword' }).catch(() => {})
        } else if (this.hasAuthenticationStatus) {
          if (this.authenticationStatus.variant === 'error') {
            this.errorMessage = this.authenticationStatus.message
            this.$store.commit('SNACKBAR', this.authenticationStatus)
          } else {
            this.$router
              .push({
                name: 'ConfirmLoginOTP',
                query: { email: this.email },
              })
              .catch(() => {})
          }
        }
      } catch {
        this.errorMessage = 'An error occurred during login. Please try again.'
        this.showSnackbar(
          'An error occurred during login. Please try again.',
          'error'
        )
      } finally {
        this.$store.commit('SET_LOADING', false)
      }
    },
    showSnackbar(text, color = 'error') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
.auth-card {
  border-radius: 18px;
  border: 1px solid #e8edf5;
  background: #ffffff;
  box-shadow: 0 18px 48px rgba(15, 42, 85, 0.12);
  padding: 28px 30px 30px;
}
.auth-head {
  text-align: center;
  margin-bottom: 18px;
}
.auth-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f2a55;
  margin: 0;
}
.auth-sub {
  font-size: 13px;
  color: #6b7a90;
  margin-top: 4px;
}
.auth-link {
  color: #034ea2;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
}
.auth-link:hover {
  text-decoration: underline;
}
.auth-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 12px;
  background: #fdecec;
  border: 1px solid #f6cccc;
  color: #b3251b;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
}
.auth-btn {
  background: linear-gradient(135deg, #034ea2 0%, #0a63c4 100%) !important;
  color: #ffffff !important;
  border-radius: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  box-shadow: 0 8px 20px rgba(3, 78, 162, 0.3);
}
.auth-btn.v-btn--disabled {
  background: #e6ebf3 !important;
  color: #4b5a70 !important;
  box-shadow: none;
}
/* Soft rounded inputs */
.auth-card ::v-deep .v-text-field--outlined .v-input__slot {
  border-radius: 10px;
}
.auth-card ::v-deep .v-text-field--outlined fieldset {
  border-color: #d6deea;
}
.auth-card ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #034ea2;
}
</style>
